import * as React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { PdfDocumentView } from "../components/documents/PdfDocumentView";
import type { EditorSteuerung } from "../components/documents/PdfEditorViewer";
import { isEditableImage, isPdfLike } from "../components/documents/documentKind";
import { PdfIcons } from "../components/documents/pdf-editor/pdfIcons";
import { DokumentTabLeiste, TabSchliessenPopup } from "../components/documents/pdf-editor/DokumentTabLeiste";
import {
    editorPfad,
    editorTabAnmelden,
    gemerkteListe,
    listeMerken,
    MAX_DOK_TABS,
    startListe,
    tabOeffnen,
    tabSchliessen,
    type TabListe,
} from "../components/documents/pdf-editor/editorTab";
import { hinweisen } from "../components/redesign/bestaetigen";
import { WindowManagerProvider } from "../components/windowManager/WindowManagerContext";
import WindowLayer from "../components/windowManager/WindowLayer";
import "../components/documents/pdf-editor/pdfEditor.css";
import { documentService } from "../services/documentService";

/**
 * PDFs und Bilder auf der ganzen Seite — eigener Browser-Tab, kein App-Rahmen.
 *
 * Aufgerufen über „In neuem Tab“ in `PdfDocumentView`: Der Nutzer will das PDF
 * groß vor sich haben, ohne Navigationsleiste links und Kopfzeile oben, aber mit
 * allem, was KMVS am PDF kann (Bearbeiten, Fassungen, Umbenennen) — die nackte
 * Datei im Browser-Viewer konnte das nicht.
 *
 * MEHRERE DOKUMENTE (User 04.10.2026: „alle Tabs … in einem Tab/PDF und man
 * nicht immer springen muss“): Oben steht eine Tab-Zeile mit allen hier
 * geöffneten Dokumenten. Öffnet KMVS ein weiteres, während dieser Tab offen
 * ist, kommt es hier dazu (`editorTab.ts`), statt einen zweiten Browser-Tab
 * aufzumachen.
 *
 * Jedes Dokument bleibt EINGEHÄNGT, auch wenn ein anderes vorn ist — nur
 * versteckt. So überstehen ungespeicherte Änderungen (samt Rückgängig-Verlauf)
 * den Tab-Wechsel ohne Rückfrage. Der Preis ist Speicher: je Tab ein PDF samt
 * Editor, deshalb höchstens `MAX_DOK_TABS`. Eingehängt wird ein Tab erst, wenn
 * er das erste Mal vorn ist — nach dem Neuladen lädt also nur das sichtbare
 * Dokument.
 *
 * Die Liste übersteht ein Neuladen (sessionStorage), die URL zeigt immer das
 * sichtbare Dokument.
 *
 * Wie `OfficeEditorPage` hängt die Route bewusst NICHT unter `<AppShell />`
 * (siehe `App.tsx`).
 */

const NICHT_DA = "Dieses Dokument gibt es nicht oder Sie dürfen es nicht sehen.";

const mitte: React.CSSProperties = {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    boxSizing: "border-box",
    fontFamily: "system-ui, sans-serif",
    color: "#5B6570",
    textAlign: "center",
};

interface TabStand {
    name?: string;
    bild?: boolean;
    ungespeichert?: boolean;
}

/** Ein Dokument der Tab-Zeile: lädt Name und Typ, zeigt dann die PDF-Ansicht. */
const DokumentInhalt: React.FC<{
    docId: number;
    startImEditor: boolean;
    sichtbar: boolean;
    steuerung: React.MutableRefObject<EditorSteuerung | null>;
    onStand: (docId: number, teil: TabStand) => void;
    onEditorVerlassen: (docId: number) => boolean;
}> = ({ docId, startImEditor, sichtbar, steuerung, onStand, onEditorVerlassen }) => {
    const [doc, setDoc] = React.useState<{ fileName: string; mimeType?: string | null; istPdf: boolean } | null>(null);
    const [fehler, setFehler] = React.useState<string | null>(null);

    React.useEffect(() => {
        let aktiv = true;
        documentService
            .getOne(docId)
            .then((d) => {
                if (!aktiv) return;
                // Bilder öffnet dieselbe Ansicht — sie hüllt sie für die Sitzung in ein PDF.
                const bild = isEditableImage(d.fileName, d.mimeType);
                setDoc({ fileName: d.fileName, mimeType: d.mimeType, istPdf: isPdfLike(d.fileName, d.mimeType) || bild });
                onStand(docId, { name: d.fileName, bild });
            })
            .catch(() => {
                // 404 heißt hier auch „anderer Mandant“ oder „keine Berechtigung“ — der
                // Grund gehört dem Server, nicht dieser Seite.
                if (aktiv) setFehler(NICHT_DA);
            });
        return () => {
            aktiv = false;
        };
        // `onStand` ist stabil.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [docId]);

    const umbenannt = React.useCallback(
        // Nach „Als PDF speichern“ ist aus dem Bild ein PDF geworden — das Symbol zieht mit.
        (fileName: string) => onStand(docId, { name: fileName, bild: isEditableImage(fileName) }),
        [docId, onStand],
    );
    const aenderungen = React.useCallback((u: boolean) => onStand(docId, { ungespeichert: u }), [docId, onStand]);
    const verlassen = React.useCallback(() => onEditorVerlassen(docId), [docId, onEditorVerlassen]);

    if (fehler) return <div style={mitte}>{fehler}</div>;
    if (!doc) return <div style={{ height: "100%" }} />;

    if (!doc.istPdf) {
        return (
            <div className="pdfx" style={mitte}>
                <div>„{doc.fileName}“ ist kein PDF und lässt sich hier nicht anzeigen.</div>
                <button
                    type="button"
                    className="pdfx-btn primary"
                    onClick={() => void documentService.openSignedDownload(docId).catch(() => undefined)}
                >
                    {PdfIcons.herunterladen}
                    Herunterladen
                </button>
            </div>
        );
    }

    return (
        <PdfDocumentView
            docId={docId}
            fileName={doc.fileName}
            mimeType={doc.mimeType}
            canEdit
            imEigenenTab
            startImEditor={startImEditor}
            onRenamed={umbenannt}
            sichtbar={sichtbar}
            onAenderungen={aenderungen}
            steuerung={steuerung}
            onEditorVerlassen={verlassen}
        />
    );
};

/**
 * Die Seite steht VOR der App-Shell und hat deren Fenster-Manager nicht. Der
 * Seiten-Organisator öffnet „Seiten einfügen aus Dokument“ aber als Fenster —
 * ohne Provider stürzte schon das Öffnen von „Seiten“ ab. Deshalb bringt die
 * Seite ihren eigenen Fenster-Manager samt Ebene mit.
 */
export const PdfDokumentPage: React.FC = () => (
    <WindowManagerProvider>
        <PdfDokumentSeite />
        <div style={{ position: "fixed", inset: 0, pointerEvents: "none" }}>
            <WindowLayer />
        </div>
    </WindowManagerProvider>
);

const PdfDokumentSeite: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    // `?bearbeiten=1`: direkt im Editor starten (User 02.10.2026).
    const [searchParams] = useSearchParams();
    const urlBearbeiten = searchParams.get("bearbeiten") === "1";
    const urlId = Number(id);
    const gueltig = Number.isInteger(urlId) && urlId > 0;

    const [liste, setListeRoh] = React.useState<TabListe>(() => startListe(gueltig ? gemerkteListe() : null, gueltig ? urlId : null));
    // Synchron lesbar: Schließen und Öffnen entscheiden am aktuellen Stand, nicht am zuletzt gerenderten.
    const listeRef = React.useRef(liste);
    const setListe = React.useCallback((neu: TabListe) => {
        listeRef.current = neu;
        setListeRoh(neu);
    }, []);
    const [stand, setStand] = React.useState<Record<number, TabStand>>({});
    const standRef = React.useRef(stand);
    standRef.current = stand;
    // Dokumente, die direkt im Editor aufgehen (und deren „Schließen“ den Tab schließt).
    const imEditorGestartet = React.useRef(new Set<number>(urlBearbeiten && gueltig ? [urlId] : []));
    // Schon einmal vorn gewesen → eingehängt (und bleibt es bis zum Schließen).
    const [eingehaengt, setEingehaengt] = React.useState<ReadonlySet<number>>(() => new Set(liste.aktiv != null ? [liste.aktiv] : []));
    const steuerungen = React.useRef(new Map<number, React.MutableRefObject<EditorSteuerung | null>>());
    const steuerungVon = (docId: number) => {
        let ref = steuerungen.current.get(docId);
        if (!ref) {
            ref = { current: null };
            steuerungen.current.set(docId, ref);
        }
        return ref;
    };
    const [schliessFrage, setSchliessFrage] = React.useState<number | null>(null);

    const onStand = React.useCallback((docId: number, teil: TabStand) => {
        setStand((s) => {
            const alt = s[docId] ?? {};
            const neu = { ...alt, ...teil };
            if (neu.name === alt.name && neu.bild === alt.bild && neu.ungespeichert === alt.ungespeichert) return s;
            return { ...s, [docId]: neu };
        });
    }, []);

    React.useEffect(() => {
        if (liste.aktiv == null) return;
        const aktiv = liste.aktiv;
        setEingehaengt((e) => (e.has(aktiv) ? e : new Set([...e, aktiv])));
    }, [liste.aktiv]);

    // Tabs, die (nach dem Neuladen) noch nicht eingehängt sind, sollen trotzdem ihren
    // Namen tragen — nur die Stammdaten holen, nicht die Datei.
    React.useEffect(() => {
        let aktiv = true;
        for (const docId of liste.tabs) {
            if (docId === liste.aktiv || eingehaengt.has(docId) || standRef.current[docId]?.name) continue;
            documentService
                .getOne(docId)
                .then((d) => aktiv && onStand(docId, { name: d.fileName, bild: isEditableImage(d.fileName, d.mimeType) }))
                .catch(() => undefined);
        }
        return () => {
            aktiv = false;
        };
        // Nur wenn sich die Tabs ändern — `eingehaengt`/`onStand` lösen kein neues Holen aus.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [liste.tabs]);

    // Die Liste übersteht ein Neuladen; die URL zeigt das sichtbare Dokument.
    React.useEffect(() => listeMerken(liste), [liste]);
    React.useEffect(() => {
        if (liste.aktiv != null && liste.aktiv !== urlId) navigate(editorPfad(liste.aktiv), { replace: true });
        // Nur dem aktiven Tab folgen — eine neue URL behandelt der Effekt darunter.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [liste.aktiv]);

    const voll = React.useCallback(
        () =>
            void hinweisen({
                titel: "Zu viele Dokumente geöffnet",
                text: `In diesem Editor-Tab sind schon ${MAX_DOK_TABS} Dokumente offen. Bitte erst eines schließen (× am Tab) und das Dokument dann noch einmal öffnen.`,
            }),
        [],
    );
    const oeffnen = React.useCallback(
        (docId: number, bearbeiten: boolean) => {
            const schonOffen = listeRef.current.tabs.includes(docId);
            const { liste: neu, voll: istVoll } = tabOeffnen(listeRef.current, docId);
            if (istVoll) {
                voll();
                return;
            }
            // Ein schon offenes Dokument wird nur nach vorn geholt — sein Zustand bleibt.
            if (bearbeiten && !schonOffen) imEditorGestartet.current.add(docId);
            setListe(neu);
        },
        [setListe, voll],
    );
    // Die URL nennt ein anderes Dokument (von Hand geändert) → als Tab dazu.
    React.useEffect(() => {
        if (gueltig && listeRef.current.aktiv !== urlId) oeffnen(urlId, urlBearbeiten);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlId]);

    // Dokumente annehmen, die KMVS aus einem anderen Browser-Tab hierher schickt.
    React.useEffect(() => (gueltig ? editorTabAnmelden(oeffnen) : undefined), [gueltig, oeffnen]);

    const wirklichSchliessen = React.useCallback(
        (docId: number) => {
            const neu = tabSchliessen(listeRef.current, docId);
            setListe(neu);
            imEditorGestartet.current.delete(docId);
            steuerungen.current.delete(docId);
            setEingehaengt((e) => new Set([...e].filter((x) => x !== docId)));
            setStand(({ [docId]: _weg, ...rest }) => rest);
            // Letztes Dokument zu: der Browser-Tab hat nichts mehr zu zeigen. Schließen
            // darf er sich nur selbst, wenn KMVS ihn geöffnet hat.
            if (neu.tabs.length === 0 && window.opener) window.close();
        },
        [setListe],
    );
    const schliessen = React.useCallback(
        (docId: number) => {
            if (standRef.current[docId]?.ungespeichert) setSchliessFrage(docId);
            else wirklichSchliessen(docId);
        },
        [wirklichSchliessen],
    );
    /** „Schließen“ im Editor eines Dokuments, das direkt im Editor aufging: der Tab geht zu. */
    const editorVerlassen = React.useCallback(
        (docId: number): boolean => {
            if (!imEditorGestartet.current.has(docId)) return false;
            // Einziges Dokument in einem direkt aufgerufenen Tab: zurück zur Leseansicht.
            if (listeRef.current.tabs.length <= 1 && !window.opener) return false;
            wirklichSchliessen(docId);
            return true;
        },
        [wirklichSchliessen],
    );
    const speichernUndSchliessen = React.useCallback(
        (docId: number) => {
            setSchliessFrage(null);
            // Das Speichern-Popup gehört dem Editor dieses Dokuments — es muss vorn sein.
            setListe({ ...listeRef.current, aktiv: docId });
            void steuerungen.current
                .get(docId)
                ?.current?.speichern()
                .then((gespeichert) => gespeichert && wirklichSchliessen(docId));
        },
        [setListe, wirklichSchliessen],
    );

    // Der Titel des Browser-Tabs ist die Beschriftung, die in der Tab-Leiste stehen bleibt.
    const aktiverName = liste.aktiv != null ? stand[liste.aktiv]?.name : undefined;
    React.useEffect(() => {
        if (aktiverName) document.title = `${aktiverName} — KMVS`;
    }, [aktiverName]);

    // Browser-Tab schließen oder neu laden mit ungespeicherter Arbeit: hier kann nur
    // der Browser selbst nachfragen (`beforeunload` lässt kein eigenes Popup zu).
    const etwasUngespeichert = Object.values(stand).some((s) => s.ungespeichert);
    React.useEffect(() => {
        if (!etwasUngespeichert) return;
        const warnen = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", warnen);
        return () => window.removeEventListener("beforeunload", warnen);
    }, [etwasUngespeichert]);

    if (!gueltig) return <div style={{ ...mitte, height: "100vh" }}>{NICHT_DA}</div>;

    const nameVon = (docId: number) => stand[docId]?.name ?? `Dokument ${docId}`;
    return (
        <div className="pdfx pdfx-seite">
            <DokumentTabLeiste
                tabs={liste.tabs.map((docId) => ({
                    id: docId,
                    name: nameVon(docId),
                    bild: stand[docId]?.bild,
                    ungespeichert: stand[docId]?.ungespeichert,
                }))}
                aktiv={liste.aktiv}
                onWaehlen={(docId) => setListe({ ...listeRef.current, aktiv: docId })}
                onSchliessen={schliessen}
            />
            {liste.tabs
                .filter((docId) => eingehaengt.has(docId) || docId === liste.aktiv)
                .map((docId) => (
                    <div
                        key={docId}
                        className="pdfx-seite-inhalt"
                        role="tabpanel"
                        aria-label={nameVon(docId)}
                        // Versteckt statt ausgehängt: der Editor behält seine ungespeicherte Arbeit.
                        style={docId === liste.aktiv ? undefined : { display: "none" }}
                    >
                        <DokumentInhalt
                            docId={docId}
                            startImEditor={imEditorGestartet.current.has(docId)}
                            sichtbar={docId === liste.aktiv}
                            steuerung={steuerungVon(docId)}
                            onStand={onStand}
                            onEditorVerlassen={editorVerlassen}
                        />
                    </div>
                ))}
            {liste.tabs.length === 0 && (
                <div className="pdfx-seite-inhalt">
                    <div style={mitte}>Kein Dokument mehr geöffnet — dieser Browser-Tab kann geschlossen werden.</div>
                </div>
            )}
            {schliessFrage != null && (
                <TabSchliessenPopup
                    name={nameVon(schliessFrage)}
                    onSpeichern={() => speichernUndSchliessen(schliessFrage)}
                    onVerwerfen={() => {
                        const docId = schliessFrage;
                        setSchliessFrage(null);
                        wirklichSchliessen(docId);
                    }}
                    onAbbrechen={() => setSchliessFrage(null)}
                />
            )}
        </div>
    );
};

export default PdfDokumentPage;
